import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './Button';
import Input from './Input';
import NumericInput from './NumericInput';
import SearchableSelect from './SearchableSelect';
import ClientCreditModal from './ClientCreditModal';
import { clientsAPI, productsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const INITIAL_FORM = {
  companyName: '',
  address: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  truckPlateNumber: '',
  truckEmptyWeight: '',
  driverFirstName: '',
  driverLastName: '',
  driverPhone: '',
  driverEmail: ''
};

const buildInitialDdp = (weighbridgeId) => ({
  transactionType: 'IMPORT',
  navire: '',
  manutentionNumber: '',
  arrivingDate: new Date().toISOString().slice(0, 10),
  weighbridgeId: weighbridgeId || '',
  lineItems: [{ productId: '', totalWeight: '' }]
});

const PeseurQuickClientModal = ({ isOpen, onClose, onClientCreated }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { getPeseurWeighbridges } = useAuth();
  const [step, setStep] = useState(1);
  const [quickClientForm, setQuickClientForm] = useState(INITIAL_FORM);
  const [ddpForm, setDdpForm] = useState(() => buildInitialDdp(''));
  const [products, setProducts] = useState([]);
  const productSelectOptions = useMemo(
    () => (products || []).map((p) => ({ value: p.id, label: p.name || p.id })),
    [products]
  );
  const [quickClientLoading, setQuickClientLoading] = useState(false);
  const [quickCreatedClientForCredit, setQuickCreatedClientForCredit] = useState(null);
  const [quickClientCreditOpen, setQuickClientCreditOpen] = useState(false);

  const resetDdpFromWeighbridges = useCallback(() => {
    const wbs = getPeseurWeighbridges();
    setDdpForm(buildInitialDdp(wbs.length === 1 ? wbs[0].id : ''));
  }, [getPeseurWeighbridges]);

  useEffect(() => {
    if (!isOpen) return;
    setQuickClientForm(INITIAL_FORM);
    resetDdpFromWeighbridges();
    setStep(1);
    productsAPI
      .getAll({ page: 1, limit: 400 })
      .then((res) => setProducts(res?.data?.products || []))
      .catch(() => setProducts([]));
  }, [isOpen, resetDdpFromWeighbridges]);

  const handleClose = () => {
    if (quickClientLoading) return;
    onClose?.();
  };

  const validateStep1 = () => {
    const f = quickClientForm;
    if (
      !f.companyName?.trim() ||
      !f.address?.trim() ||
      !f.contactName?.trim() ||
      !f.contactEmail?.trim() ||
      !f.contactPhone?.trim()
    ) {
      toast.error(t('clients.fillRequired'));
      return false;
    }
    return true;
  };

  const validateStep2Truck = () => {
    const plate = quickClientForm.truckPlateNumber?.trim() || '';
    const df = quickClientForm.driverFirstName?.trim() || '';
    const dl = quickClientForm.driverLastName?.trim() || '';
    if (plate) {
      if (!df || !dl) {
        toast.error(t('weighing.quickClientDriverRequiredWithPlate'));
        return false;
      }
    }
    const emptyW = quickClientForm.truckEmptyWeight?.toString().trim();
    if (emptyW) {
      const n = parseFloat(emptyW.replace(',', '.'));
      if (Number.isNaN(n) || n < 0) {
        toast.error(t('weighing.quickClientInvalidEmptyWeight'));
        return false;
      }
    }
    return true;
  };

  const goNextFromStep1 = () => {
    if (!validateStep1()) return;
    setStep(2);
  };

  const goNextFromStep2 = () => {
    if (!validateStep2Truck()) return;
    setStep(3);
  };

  const goBack = () => {
    if (quickClientLoading) return;
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const validateStep3Ddp = () => {
    const wbs = getPeseurWeighbridges();
    if (!ddpForm.navire?.trim() || !ddpForm.manutentionNumber?.trim() || !ddpForm.arrivingDate?.trim()) {
      toast.error(t('weighing.quickClientDdpIncomplete'));
      return false;
    }
    if (wbs.length > 1 && !ddpForm.weighbridgeId?.trim()) {
      toast.error(t('weighing.quickClientWeighbridgeRequired'));
      return false;
    }
    if (!ddpForm.lineItems?.length) {
      toast.error(t('ddp.lineItemsRequired'));
      return false;
    }
    for (let i = 0; i < ddpForm.lineItems.length; i += 1) {
      const row = ddpForm.lineItems[i];
      if (!row.productId?.trim()) {
        toast.error(t('ddp.selectProduct'));
        return false;
      }
      const w = parseFloat(String(row.totalWeight).replace(',', '.'));
      if (!Number.isFinite(w) || w <= 0) {
        toast.error(t('ddp.validTotalWeight'));
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep1() || !validateStep2Truck() || !validateStep3Ddp()) return;

    const plate = quickClientForm.truckPlateNumber?.trim() || '';
    const df = quickClientForm.driverFirstName?.trim() || '';
    const dl = quickClientForm.driverLastName?.trim() || '';

    const emptyW = quickClientForm.truckEmptyWeight?.toString().trim();
    let truckEmptyWeight;
    if (emptyW) {
      const n = parseFloat(emptyW.replace(',', '.'));
      if (Number.isNaN(n) || n < 0) {
        toast.error(t('weighing.quickClientInvalidEmptyWeight'));
        return;
      }
      truckEmptyWeight = n;
    }

    const wbs = getPeseurWeighbridges();
    const resolvedWb =
      wbs.length === 1 ? wbs[0].id : ddpForm.weighbridgeId?.trim() || '';

    setQuickClientLoading(true);
    try {
      const res = await clientsAPI.peseurQuickCreate({
        companyName: quickClientForm.companyName.trim(),
        address: quickClientForm.address.trim(),
        contactName: quickClientForm.contactName.trim(),
        contactEmail: quickClientForm.contactEmail.trim(),
        contactPhone: quickClientForm.contactPhone.trim(),
        truckPlateNumber: plate || undefined,
        truckEmptyWeight,
        driverFirstName: df || undefined,
        driverLastName: dl || undefined,
        driverPhone: quickClientForm.driverPhone?.trim() || undefined,
        driverEmail: quickClientForm.driverEmail?.trim() || undefined,
        transactionType: ddpForm.transactionType,
        navire: ddpForm.navire.trim(),
        manutentionNumber: ddpForm.manutentionNumber.trim(),
        arrivingDate: ddpForm.arrivingDate,
        weighbridgeId: resolvedWb || undefined,
        lineItems: ddpForm.lineItems.map((row) => ({
          productId: row.productId.trim(),
          totalWeight: parseFloat(String(row.totalWeight).replace(',', '.'))
        }))
      });

      const newClient = res?.data?.client || null;
      toast.success(t('weighing.quickClientSuccess'));
      onClose?.();

      if (newClient?.id) {
        setQuickCreatedClientForCredit({
          id: newClient.id,
          companyName: newClient.companyName
        });
        setQuickClientCreditOpen(true);
      }

      onClientCreated?.(newClient);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('weighing.messages.failedToCreateTicket'));
    } finally {
      setQuickClientLoading(false);
    }
  };

  const stepLabel =
    step === 1
      ? t('weighing.quickClientStepClient')
      : step === 2
        ? t('weighing.quickClientStepTruck')
        : t('weighing.quickClientStepDdp');

  const peseurWeighbridges = getPeseurWeighbridges();

  const updateDdpLine = (index, field, value) => {
    setDdpForm((prev) => {
      const lineItems = [...(prev.lineItems || [])];
      lineItems[index] = { ...lineItems[index], [field]: value };
      return { ...prev, lineItems };
    });
  };

  const addDdpLine = () => {
    setDdpForm((prev) => ({
      ...prev,
      lineItems: [...(prev.lineItems || []), { productId: '', totalWeight: '' }]
    }));
  };

  const removeDdpLine = (index) => {
    setDdpForm((prev) => {
      const lineItems = [...(prev.lineItems || [])];
      if (lineItems.length <= 1) return prev;
      lineItems.splice(index, 1);
      return { ...prev, lineItems };
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          <span>
            {t('weighing.quickClientTitle')}
            <span className="block text-sm font-normal text-gray-500 mt-0.5">{stepLabel}</span>
          </span>
        }
        size="md"
        footer={
          <>
            <Button variant="outline" type="button" disabled={quickClientLoading} onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            {step === 1 ? (
              <Button type="button" onClick={goNextFromStep1}>
                {t('weighing.quickClientNext')}
              </Button>
            ) : step === 2 ? (
              <>
                <Button variant="outline" type="button" disabled={quickClientLoading} onClick={goBack}>
                  {t('common.back')}
                </Button>
                <Button type="button" onClick={goNextFromStep2}>
                  {t('weighing.quickClientNext')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" type="button" disabled={quickClientLoading} onClick={goBack}>
                  {t('common.back')}
                </Button>
                <Button type="button" loading={quickClientLoading} onClick={handleSubmit}>
                  {t('weighing.quickClientSave')}
                </Button>
              </>
            )}
          </>
        }
      >
        {step === 1 ? (
          <div className="space-y-3">
            <Input
              label={t('clients.companyName')}
              value={quickClientForm.companyName}
              onChange={(e) => setQuickClientForm((prev) => ({ ...prev, companyName: e.target.value }))}
              required
            />
            <Input
              label={t('clients.contactName')}
              value={quickClientForm.contactName}
              onChange={(e) => setQuickClientForm((prev) => ({ ...prev, contactName: e.target.value }))}
              required
            />
            <Input
              label={t('clients.contactEmail')}
              type="email"
              value={quickClientForm.contactEmail}
              onChange={(e) => setQuickClientForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
              required
            />
            <Input
              label={t('clients.contactPhone')}
              value={quickClientForm.contactPhone}
              onChange={(e) => setQuickClientForm((prev) => ({ ...prev, contactPhone: e.target.value }))}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('clients.address')} *</label>
              <textarea
                value={quickClientForm.address}
                onChange={(e) => setQuickClientForm((prev) => ({ ...prev, address: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <p className="text-xs text-gray-600">{t('clients.requestCreditTopupHint')}</p>
          </div>
        ) : step === 2 ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{t('weighing.quickClientTruckHint')}</p>
            <Input
              label={t('trucks.plateNumber')}
              value={quickClientForm.truckPlateNumber}
              onChange={(e) => setQuickClientForm((prev) => ({ ...prev, truckPlateNumber: e.target.value }))}
            />
            <Input
              label={`${t('trucks.emptyWeight')} (${t('common.optional')})`}
              type="number"
              step="0.01"
              min="0"
              value={quickClientForm.truckEmptyWeight}
              onChange={(e) => setQuickClientForm((prev) => ({ ...prev, truckEmptyWeight: e.target.value }))}
            />
            <div className="pt-2 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-800 mb-2">{t('drivers.title')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label={t('drivers.firstName')}
                  value={quickClientForm.driverFirstName}
                  onChange={(e) => setQuickClientForm((prev) => ({ ...prev, driverFirstName: e.target.value }))}
                />
                <Input
                  label={t('drivers.lastName')}
                  value={quickClientForm.driverLastName}
                  onChange={(e) => setQuickClientForm((prev) => ({ ...prev, driverLastName: e.target.value }))}
                />
              </div>
              <Input
                className="mt-3"
                label={`${t('drivers.phone')} (${t('common.optional')})`}
                value={quickClientForm.driverPhone}
                onChange={(e) => setQuickClientForm((prev) => ({ ...prev, driverPhone: e.target.value }))}
              />
              <Input
                className="mt-3"
                label={`${t('drivers.email')} (${t('common.optional')})`}
                type="email"
                value={quickClientForm.driverEmail}
                onChange={(e) => setQuickClientForm((prev) => ({ ...prev, driverEmail: e.target.value }))}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{t('weighing.quickClientDdpHint')}</p>
            {peseurWeighbridges.length > 1 ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('weighing.selectWeighbridge')} *</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                  value={ddpForm.weighbridgeId}
                  onChange={(e) => setDdpForm((prev) => ({ ...prev, weighbridgeId: e.target.value }))}
                >
                  <option value="">{t('weighing.selectWeighbridge')}</option>
                  {peseurWeighbridges.map((wb) => (
                    <option key={wb.id} value={wb.id}>
                      {wb.name}
                      {wb.port?.name ? ` — ${wb.port.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('ddp.transactionType')} *</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                value={ddpForm.transactionType}
                onChange={(e) => setDdpForm((prev) => ({ ...prev, transactionType: e.target.value }))}
              >
                <option value="IMPORT">{t('loads.transactionTypes.import')}</option>
                <option value="EXPORT">{t('loads.transactionTypes.export')}</option>
              </select>
            </div>
            <Input
              label={t('ddp.navire')}
              value={ddpForm.navire}
              onChange={(e) => setDdpForm((prev) => ({ ...prev, navire: e.target.value }))}
              required
            />
            <Input
              label={t('ddp.manutentionNumber')}
              value={ddpForm.manutentionNumber}
              onChange={(e) => setDdpForm((prev) => ({ ...prev, manutentionNumber: e.target.value }))}
              required
            />
            <Input
              label={t('ddp.arrivingDate')}
              type="date"
              value={ddpForm.arrivingDate}
              onChange={(e) => setDdpForm((prev) => ({ ...prev, arrivingDate: e.target.value }))}
              required
            />
            <div className="pt-2 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-800 mb-2">{t('ddp.lineItems')}</p>
              {ddpForm.lineItems.map((row, index) => (
                <div key={index} className="flex flex-col sm:flex-row gap-2 mb-2 items-start sm:items-end">
                  <div className="flex-1 w-full min-w-0">
                    <SearchableSelect
                      label={`${t('ddp.lineItemNumber', { index: index + 1 })} — ${t('ddp.product')}`}
                      name={`quickClientDdpProduct-${index}`}
                      value={row.productId}
                      onChange={(e) => updateDdpLine(index, 'productId', e.target.value)}
                      options={productSelectOptions}
                      placeholder={t('ddp.selectProduct')}
                      disabled={productSelectOptions.length === 0}
                    />
                  </div>
                  <div className="w-full sm:w-36">
                    <label className="block text-xs text-gray-600 mb-0.5">{t('ddp.totalWeight')} (t)</label>
                    <NumericInput
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      value={row.totalWeight}
                      onChange={(e) => updateDdpLine(index, 'totalWeight', e.target.value)}
                    />
                  </div>
                  {ddpForm.lineItems.length > 1 ? (
                    <button
                      type="button"
                      className="text-sm text-red-600 hover:text-red-800 px-2 py-2"
                      onClick={() => removeDdpLine(index)}
                    >
                      {t('ddp.removeLineItem')}
                    </button>
                  ) : (
                    <span className="sm:w-16" />
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" className="mt-1" onClick={addDdpLine}>
                {t('ddp.addLineItem')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ClientCreditModal
        isOpen={quickClientCreditOpen}
        onClose={() => {
          setQuickClientCreditOpen(false);
          setQuickCreatedClientForCredit(null);
        }}
        client={quickCreatedClientForCredit}
        successBannerMessage={quickClientCreditOpen ? t('weighing.quickClientSuccess') : ''}
        onCreditUpdated={() => {
          if (quickCreatedClientForCredit) {
            onClientCreated?.(quickCreatedClientForCredit);
          }
        }}
      />
    </>
  );
};

export default PeseurQuickClientModal;
